(() => {
  "use strict";
  if (window.__spiritInkAccountInstalled) return;
  window.__spiritInkAccountInstalled = true;

  const config = window.SpiritInkAccountConfig || {};
  const SESSION_KEY = "spiritInkSchoolSession:v2";
  const STORE_PRODUCTS_KEY = "spiritInkSchoolStoreProducts:v1";
  const BOOTSTRAP_PATH = String(config.bootstrapPath || "/apps/spirit-ink/account/bootstrap");
  const BIND_PATH = String(config.bindOnboardingPath || "/apps/spirit-ink/account/bind-onboarding");
  const MAGIC_PREVIEW_PATH = "/pages/magic-preview";
  const customerGid = config.loggedIn === true ? String(config.customerGid || "") : "";
  const schoolPattern = /^sch_[a-f0-9]{24}$/i;
  const editable = new Set(["Draft", "Published"]);
  const STATUS_TEXT = {
    Draft: "Not submitted yet",
    Published: "Live",
    "Pending Approval": "Waiting for review",
    "Update Pending Approval": "Changes waiting for review",
  };
  const clearSession = () => {
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
      window.sessionStorage.removeItem("spiritInkSchoolProvision:v2");
      window.sessionStorage.removeItem(STORE_PRODUCTS_KEY);
      for (let index = window.localStorage.length - 1; index >= 0; index--) {
        const key = window.localStorage.key(index);
        if (key?.startsWith("spirit-ink-magic-preview-v3")) window.localStorage.removeItem(key);
      }
    } catch {}
    delete document.documentElement.dataset.siSchoolStoreSession;
  };
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || "null");
    if (!customerGid || (stored && (stored.customer_gid !== customerGid
      || !(Date.parse(stored.session_expires_at) > Date.now())))) clearSession();
  } catch { clearSession(); }
  const publicError = (code, status) => {
    if (status === 401) return "Your sign-in has expired. Sign in again to continue.";
    const messages = {
      invalid_submission_id: "This School Store link is invalid. Please return to the onboarding form.",
      onboarding_timeout: "We couldn’t find your completed form yet. Check that it was submitted, then try this link again.",
      onboarding_ownership_conflict: "This form was completed with a different email address. Sign in with that email to continue.",
      school_not_editable: "This School Store can’t be changed right now. It may be waiting for review.",
      draft_not_editable: "Only an unsubmitted draft can be renamed or deleted. Refresh to see its current status.",
      invalid_school_name: "Enter a name for your School Store.",
      ambiguous_active_sessions: "We couldn’t safely reopen this School Store. Please contact Spirit Ink for help.",
      store_delete_failed: "We couldn’t delete this School Store safely. Please try again.",
    };
    return messages[code] || "We couldn’t connect to your School Store. Please try again shortly.";
  };

  const safeSameOriginPath = (value, fallback) => {
    try {
      const url = new URL(String(value || fallback), window.location.origin);
      return url.origin === window.location.origin ? `${url.pathname}${url.search}` : fallback;
    } catch {
      return fallback;
    }
  };

  const fetchJson = async (path, params = {}, timeoutMs = 15000, body = null) => {
    const url = new URL(safeSameOriginPath(path, path), window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      const normalized = String(value || "").trim();
      if (normalized) url.searchParams.set(key, normalized);
    });
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await window.fetch(url.href, {
        method: body ? "POST" : "GET",
        headers: { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.success !== true) {
        const error = new Error(publicError(data?.error, response.status));
        error.status = response.status;
        error.code = String(data?.error || "account_request_failed");
        throw error;
      }
      if (data.customer_gid !== customerGid) {
        clearSession();
        const error = new Error(publicError("", 401));
        error.status = 401;
        throw error;
      }
      return data;
    } finally { window.clearTimeout(timer); }
  };

  const writeSchoolSession = (data, expectedSchoolId) => {
    const schoolId = String(data?.school_id || "").trim();
    const sessionId = String(data?.session_id || "").trim();
    const sessionToken = String(data?.session_token || "").trim();
    if (!schoolPattern.test(schoolId) || schoolId !== expectedSchoolId
      || !/^ses_[a-f0-9]{24}$/i.test(sessionId) || !/^[a-f0-9]{64}$/i.test(sessionToken)
      || data?.authenticated !== true || data?.customer_gid !== customerGid
      || !editable.has(data?.school_status) || !(Date.parse(data?.session_expires_at) > Date.now())) {
      clearSession();
      throw new Error("Spirit Ink could not create a secure School Store session.");
    }
    const session = {
      customer_gid: customerGid,
      school_id: schoolId,
      session_id: sessionId,
      session_token: sessionToken,
      school_status: String(data?.school_status || "").trim(),
      school_name: String(data?.school_name || "").trim(),
      store_url: String(data?.store_url || "").trim(),
      session_expires_at: String(data?.session_expires_at || "").trim(),
    };
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    document.documentElement.dataset.siSchoolStoreSession = "active";
    return session;
  };

  const setStatus = (message, kind = "loading") => {
    const node = document.querySelector("[data-school-store-status], [data-si-account-status]");
    if (!node) return;
    node.dataset.status = kind;
    node.textContent = String(message || "");
    node.hidden = !message;
    node.setAttribute("role", kind === "error" ? "alert" : "status");
  };
  const setVisible = (selector, visible) => {
    document.querySelectorAll(selector).forEach((node) => { node.hidden = !visible; });
  };
  const showError = (error) => {
    const mismatch = error?.code === "onboarding_ownership_conflict";
    const expired = error?.status === 401;
    setStatus(publicError(error?.code, error?.status), "error");
    setVisible("[data-si-account-login]", expired);
    setVisible("[data-si-account-retry]", !expired && !mismatch);
    setVisible("[data-si-account-switch]", mismatch);
    setVisible("[data-si-account-contact]", mismatch);
    setVisible("[data-si-account-start-link]", !expired);
  };
  let openingSchool = false;

  const manageSchool = (school, action, trigger) => {
    const renaming = action === "RENAME_DRAFT";
    const deletingDraft = action === "DELETE_DRAFT";
    const deletingStore = action === "DELETE_STORE";
    const allowed = (school.status === "Draft" && (renaming || deletingDraft))
      || (school.status === "Published" && deletingStore);
    if (openingSchool || !allowed) return;
    const deleting = deletingDraft || deletingStore;
    const dialog = document.createElement("dialog");
    dialog.className = "si-draft-dialog";
    dialog.setAttribute("aria-labelledby", "si-draft-dialog-title");
    const form = document.createElement("form");
    const title = document.createElement("h2");
    title.id = "si-draft-dialog-title";
    title.textContent = deletingStore ? "Delete this School Store?" : deletingDraft ? "Delete this draft?" : "Rename your store";
    const description = document.createElement("p");
    description.textContent = deletingStore
      ? `“${school.school_name}” will be removed from Spirit Ink. This cannot be undone.`
      : deletingDraft
        ? `“${school.school_name}” will be removed from your account. You won’t be able to continue this draft.`
        : "Choose the name shown for this School Store.";
    form.append(title, description);
    let input;
    if (renaming) {
      const label = document.createElement("label");
      label.textContent = "Store name";
      input = document.createElement("input");
      input.type = "text";
      input.name = "school_name";
      input.required = true;
      input.maxLength = 80;
      input.value = school.school_name;
      label.append(input);
      form.append(label);
    }
    const error = document.createElement("p");
    error.className = "si-draft-dialog__error";
    error.setAttribute("role", "alert");
    error.hidden = true;
    const actions = document.createElement("div");
    actions.className = "si-draft-dialog__actions";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "button button--secondary";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => dialog.close());
    const save = document.createElement("button");
    save.type = "submit";
    save.className = deleting ? "button si-draft-dialog__delete" : "button button--primary";
    const submitLabel = deletingStore ? "Delete store" : deletingDraft ? "Delete draft" : "Save name";
    save.textContent = submitLabel;
    actions.append(cancel, save);
    form.append(error, actions);
    dialog.append(form);
    let saving = false;
    dialog.addEventListener("cancel", event => { if (saving) event.preventDefault(); });
    dialog.addEventListener("close", () => { dialog.remove(); trigger?.focus(); }, { once: true });
    form.addEventListener("submit", async event => {
      event.preventDefault();
      if (saving) return;
      const name = input?.value.replace(/\s+/g, " ").trim() || "";
      if (renaming && !name) { input.setCustomValidity("Enter a store name."); input.reportValidity(); return; }
      saving = true;
      openingSchool = true;
      save.disabled = cancel.disabled = true;
      if (input) input.disabled = true;
      save.textContent = deleting ? "Deleting…" : "Saving…";
      error.hidden = true;
      try {
        const result = await fetchJson(BOOTSTRAP_PATH, {}, 15000, {
          action, school_id: school.school_id, ...(renaming ? { school_name: name } : {}),
        });
        if (result.school_id !== school.school_id || result.management_action !== action) throw new Error();
        try {
          const current = JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || "null");
          if (current?.school_id === school.school_id) {
            if (deleting) clearSession();
            else window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...current, school_name: result.school_name }));
          }
        } catch {}
        dialog.close();
        await loadAccountState();
        setStatus(deletingStore ? "School Store deleted." : deletingDraft ? "Draft deleted." : "Store name updated.", "success");
      } catch (failure) {
        if (dialog.isConnected) {
          error.textContent = publicError(failure?.code, failure?.status);
          error.hidden = false;
        } else showError(failure);
      } finally {
        saving = false;
        openingSchool = false;
        save.disabled = cancel.disabled = false;
        if (input) input.disabled = false;
        save.textContent = submitLabel;
      }
    });
    input?.addEventListener("input", () => input.setCustomValidity(""));
    document.body.append(dialog);
    dialog.showModal();
    if (input) { input.focus(); input.select(); } else cancel.focus();
  };

  const renderOwnedSchools = (schools) => {
    const root = document.querySelector("[data-si-account-schools]");
    if (!root) return;
    root.replaceChildren();
    const statuses = new Set(["Draft", "Published", "Pending Approval", "Update Pending Approval"]);
    if (!Array.isArray(schools) || schools.some(school => !school
      || !schoolPattern.test(school.school_id || "") || !String(school.school_name || "").trim()
      || !statuses.has(school.status))
      || new Set(schools.map(school => school.school_id)).size !== schools.length) {
      throw new Error("Invalid School Store list");
    }
    const list = schools;
    if (!list.length) {
      root.hidden = true;
      return;
    }
    root.hidden = false;
    const heading = document.createElement("h2");
    heading.className = "si-account-schools__title";
    heading.textContent = "Your School Stores";
    const rows = document.createElement("ul");
    rows.className = "si-account-schools__list";
    rows.setAttribute("role", "list");

    const openSchool = async (school, schoolId) => {
      if (openingSchool || !editable.has(school.status)) return;
      openingSchool = true;
      rows.querySelectorAll("button").forEach(node => { node.disabled = true; });
      setStatus("Opening your School Store…");
      try {
        const data = await fetchJson(BOOTSTRAP_PATH, { school_id: schoolId });
        writeSchoolSession(data, schoolId);
        window.location.assign(MAGIC_PREVIEW_PATH);
      } catch (error) {
        openingSchool = false;
        renderOwnedSchools(list);
        showError(error);
      }
    };

    const actionButton = (label, onClick, className = "") => {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = label;
      if (className) button.className = className;
      button.addEventListener("click", onClick);
      return button;
    };

    list.forEach((school) => {
      const schoolId = String(school?.school_id || "").trim();
      if (!schoolId) return;
      const item = document.createElement("li");
      item.className = "si-account-school";
      const text = document.createElement("div");
      const title = document.createElement("h3");
      title.className = "si-account-school__name";
      title.textContent = String(school?.school_name || "School Store");
      const status = document.createElement("p");
      status.className = "si-account-school__status";
      status.textContent = STATUS_TEXT[school.status] || "";
      text.append(title, status);
      item.append(text);
      const actions = document.createElement("div");
      actions.className = "si-account-school__actions";

      if (editable.has(school.status)) {
        const menu = document.createElement("details");
        menu.className = "si-account-school__menu";
        const toggle = document.createElement("summary");
        toggle.textContent = "⋯";
        toggle.setAttribute("aria-label", `Options for ${school.school_name}`);
        const choices = document.createElement("div");
        choices.className = "si-account-school__choices";

        if (school.status === "Published") {
          const rawStoreUrl = String(school.store_url || "").trim();
          const storePath = rawStoreUrl ? safeSameOriginPath(rawStoreUrl, "") : "";
          if (storePath) {
            choices.append(actionButton("Go to store", () => window.location.assign(storePath)));
          }
        } else {
          choices.append(actionButton("Continue setup", () => { menu.open = false; openSchool(school, schoolId); }));
        }

        const manage = document.createElement("div");
        manage.className = "si-account-school__manage";
        const manageChoices = document.createElement("div");
        manageChoices.className = "si-account-school__manage-choices";
        manageChoices.hidden = true;
        const manageToggle = actionButton("Manage your store", () => {
          manageChoices.hidden = !manageChoices.hidden;
          manageToggle.setAttribute("aria-expanded", String(!manageChoices.hidden));
        });
        manageToggle.setAttribute("aria-expanded", "false");
        if (school.status === "Published") {
          manageChoices.append(
            actionButton("Add products", () => { menu.open = false; openSchool(school, schoolId); }),
            actionButton("Delete store", () => { menu.open = false; manageSchool(school, "DELETE_STORE", toggle); }, "si-account-school__danger"),
          );
        } else {
          manageChoices.append(
            actionButton("Rename store", () => { menu.open = false; manageSchool(school, "RENAME_DRAFT", toggle); }),
            actionButton("Delete draft", () => { menu.open = false; manageSchool(school, "DELETE_DRAFT", toggle); }, "si-account-school__danger"),
          );
        }
        manage.append(manageToggle, manageChoices);
        choices.append(manage);
        menu.append(toggle, choices);
        menu.addEventListener("toggle", () => {
          if (menu.open) rows.querySelectorAll(".si-account-school__menu").forEach(other => { if (other !== menu) other.open = false; });
        });
        menu.addEventListener("keydown", event => { if (event.key === "Escape") { menu.open = false; toggle.focus(); } });
        actions.append(menu);
      }
      item.append(actions);
      rows.appendChild(item);
    });
    root.append(heading, rows);
  };

  const bindOnboardingIfPresent = async () => {
    const page = document.querySelector("[data-school-store-start]");
    if (!page) return false;
    const url = new URL(window.location.href);
    const submissionId = String(url.searchParams.get("submission_id") || "").trim();
    if (!submissionId || submissionId.length > 160 || !/^[A-Za-z0-9_-]+$/.test(submissionId)
      || url.searchParams.getAll("submission_id").length !== 1) {
      throw Object.assign(new Error(), { code: "invalid_submission_id" });
    }
    setStatus("Preparing your School Store…", "loading");
    const deadline = Date.now() + 60000;
    let bound;
    while (Date.now() < deadline) {
      try {
        bound = await fetchJson(BIND_PATH, { submission_id: submissionId }, Math.min(15000, deadline - Date.now()));
        break;
      } catch (error) {
        if (error.code !== "onboarding_processing") throw error;
        const remaining = deadline - Date.now();
        if (remaining > 0) await new Promise(resolve => window.setTimeout(resolve, Math.min(2000, remaining)));
      }
    }
    if (!bound) throw Object.assign(new Error(), { code: "onboarding_timeout" });
    if (!schoolPattern.test(bound?.school_id || "") || bound.submission_id !== submissionId) {
      throw new Error("Invalid onboarding response");
    }
    const session = await fetchJson(BOOTSTRAP_PATH, { school_id: bound.school_id });
    writeSchoolSession(session, bound.school_id);
    url.searchParams.delete("submission_id");
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    window.location.replace(MAGIC_PREVIEW_PATH);
    return true;
  };

  const loadAccountState = async () => {
    const root = document.querySelector("[data-si-account-schools]");
    if (!root) return;
    const state = await fetchJson(BOOTSTRAP_PATH);
    renderOwnedSchools(state.schools);
    setStatus("");
    const onboarding = document.querySelector("[data-si-account-create]");
    if (onboarding) onboarding.hidden = false;
  };

  const start = async () => {
    document.querySelectorAll("[data-si-account-login]").forEach(link => {
      const login = new URL("/customer_authentication/login", window.location.origin);
      login.searchParams.set("return_to", window.location.pathname + window.location.search);
      link.href = login.href;
    });
    document.querySelectorAll("[data-si-account-switch]").forEach(link => {
      const logout = new URL("/account/logout", window.location.origin);
      logout.searchParams.set("return_url", window.location.pathname + window.location.search);
      link.href = logout.href;
    });
    document.querySelector("[data-si-account-retry]")?.addEventListener("click", () => window.location.reload());
    if (!customerGid) return;
    try {
      if (await bindOnboardingIfPresent()) return;
      await loadAccountState();
      window.dispatchEvent(new CustomEvent("spirit-ink:account-ready"));
    } catch (error) {
      showError(error);
    }
  };
  window.addEventListener("pageshow", event => { if (event.persisted) window.location.reload(); });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
